import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'
import type { DropdownOption } from 'types/ui'
import { loadBusinessUsers } from '@disco/state-management/actions/BusinessUsersActions'
import { clientLibraryUpdate } from '@disco/state-management/actions/client-library-list-actions'
import { LICENSE_REQUEST } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import { getBusinessUsers } from '@disco/state-management/selectors/user'
import { getUserDropdownObject } from '@disco/utils/user'
import Row from 'components/common/row'
import UserSelector from 'components/common/user-selector'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ChoiceList from 'components/ui/choice-list'
import Label from 'components/ui/label'
import PageContainer from 'components/ui/page-container'
import { useFormState } from 'hooks/use-form-state'
type FormFields =
  | 'enable_email_notifications'
  | 'enable_push_notifications'
  | 'notify_user_ids'
const FORM_FIELDS: FormFields[] = [
  'enable_email_notifications',
  'enable_push_notifications',
  'notify_user_ids',
]

const LibrariesLibraryNotifications = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const dispatch = useDispatch()
  const library: ClientLibrary = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )
  const licenseRequestEnabled: boolean = useFeatureEnabled(LICENSE_REQUEST)
  const [sortedUsers, setSortedUsers] = useState([])
  const [chosenUsers, setChosenUsers] = useState<DropdownOption[]>([])
  const businessUsers = useSelector(getBusinessUsers)
  const [formState, setFormState, pendingChanges] = useFormState(
    library,
    FORM_FIELDS,
  )

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(clientLibraryUpdate(libraryId, formState))
  }

  const onUserSelect = users => {
    const newNotifyUserIds = users.map(({ value }) => value)
    setFormState('notify_user_ids', newNotifyUserIds)
    const newChosenUsers = sortedUsers
      .map(user => {
        if (newNotifyUserIds.includes(user.id)) {
          return getUserDropdownObject(user)
        }
      })
      .filter(Boolean)
    setChosenUsers([...newChosenUsers])
  }

  useEffect(() => {
    if (
      library &&
      !library.loading &&
      !sortedUsers.length &&
      !businessUsers.loading &&
      !businessUsers.loaded
    ) {
      dispatch(loadBusinessUsers())
    }
  }, [library])
  useEffect(() => {
    const users = businessUsers.users

    if (users.length && !sortedUsers.length) {
      const normalizedUsers = users.map(u => {
        const { user } = u

        if (user.first_name) {
          return { ...user, last_name: `${user.last_name} (${user.email})` }
        }

        return user
      })
      const chosenUsers = normalizedUsers
        .map(user => {
          if (library?.notify_user_ids?.includes(user.id)) {
            return getUserDropdownObject(user)
          }
        })
        .filter(user => Boolean(user))
      setChosenUsers(chosenUsers)
      setSortedUsers(normalizedUsers)
    }
  }, [businessUsers])
  return (
    <PageContainer>
      {library && !library['loading'] && (
        <DocumentHeadTags
          title={`${library['name']} | Notifications | DISCO`}
        />
      )}
      <PageContainer.Section title="Notifications">
        <Row columnsSize={[9]}>
          <div>
            <ChoiceList
              disabled={!library && library['loading']}
              multiple={false}
              title="Choose how you’d like notifications delivered:"
              items={[
                {
                  label: 'In-app notifications',
                  value: 'push',
                  onChange: () => {
                    setFormState('enable_push_notifications', true)
                    setFormState('enable_email_notifications', false)
                  },
                },
                {
                  label: 'In-app notifications and email notifications',
                  value: 'push&email',
                  onChange: () => {
                    setFormState('enable_email_notifications', true)
                    setFormState('enable_push_notifications', true)
                  },
                },
              ]}
              selected={
                formState['enable_email_notifications'] ? 'push&email' : 'push'
              }
            />
          </div>
        </Row>
        <Row columnsSize={[9]}>
          <div>
            <Label
              caption={
                licenseRequestEnabled
                  ? 'Send notifications of access requests and license request form submissions to:'
                  : 'Send notifications of access requests to:'
              }
            />
            <UserSelector
              disabled={library && library['loading']}
              users={sortedUsers}
              chosenUsers={chosenUsers}
              usersLoading={businessUsers.loading}
              onUserSelect={onUserSelect}
            />
          </div>
        </Row>
      </PageContainer.Section>
      <PageContainer.Footer inline>
        <ButtonContainer>
          <Button
            appearance="primary"
            caption="Update Catalog settings"
            disabled={!library || library.loading || !pendingChanges}
            onClick={handleSubmit}
          />
        </ButtonContainer>
      </PageContainer.Footer>
    </PageContainer>
  )
}

export default LibrariesLibraryNotifications
